import React, { useRef, useEffect, useState, ReactNode, MouseEvent, TouchEvent } from "react";
import ReactDOM from "react-dom";

type ModalSize = "sm" | "md" | "lg" | "xl" | "full";
type ModalPosition = "center" | "top" | "bottom";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  showCloseButton?: boolean;
  size?: ModalSize;
  position?: ModalPosition;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  title?: string;
  ariaLabel?: string;
  preventScroll?: boolean;
  transitionDuration?: number;
  portalClassName?: string;
  draggable?: boolean; // Nuevo prop para habilitar el modal draggable
}

const sizeClasses: Record<ModalSize, string> = {
  sm: "max-w-[95vw] w-full sm:max-w-[440px]",
  md: "max-w-[95vw] w-full sm:max-w-[540px]",
  lg: "max-w-[95vw] w-full sm:max-w-[720px]",
  xl: "max-w-[95vw] w-full sm:max-w-[920px]",
  full: "w-full h-full",
};

const positionClasses: Record<ModalPosition, string> = {
  center: "items-center justify-center",
  top: "items-start justify-center pt-4 sm:pt-8",
  bottom: "items-end justify-center pb-4 sm:pb-8",
};

const CloseButton: React.FC<{ onClose: () => void; size: ModalSize }> = ({ onClose, size }) => (
  <button
    onClick={onClose}
    aria-label="Cerrar modal"
    className={`flex items-center justify-center rounded-full bg-gray-100 text-gray-400 transition-all hover:bg-gray-200 hover:text-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700 dark:hover:text-white ${
      size === "full" ? "m-4 h-12 w-12" : "h-9 w-9"
    }`}
  >
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className={`${size === "full" ? "h-6 w-6" : "h-5 w-5"}`}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  </button>
);

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  className = "",
  showCloseButton = true,
  size = "md",
  position = "center",
  closeOnBackdropClick = true,
  closeOnEscape = true,
  title,
  ariaLabel,
  preventScroll = true,
  transitionDuration = 300,
  portalClassName = "",
  draggable = false,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const lastActiveElement = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(isOpen);
  const [animate, setAnimate] = useState(false);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const [availableHeight, setAvailableHeight] = useState(0);

  // Estados para manejar el drag
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const dragStart = useRef<{ x: number; y: number } | null>(null);

  // Crear el contenedor del portal (si no existe)
  useEffect(() => {
    let container = document.getElementById("modal-root");
    let shouldRemove = false;
    if (!container) {
      container = document.createElement("div");
      container.id = "modal-root";
      document.body.appendChild(container);
      shouldRemove = true;
    }
    setPortalContainer(container);
    return () => {
      if (shouldRemove && container && container.parentNode === document.body && container.childElementCount === 0) {
        document.body.removeChild(container);
      }
    };
  }, []);

  // Manejo de montaje/desmontaje y animación
  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      setTimeout(() => setAnimate(true), 20);
      const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      const currentHeaderHeight = headerRef.current ? headerRef.current.offsetHeight : 0;
      // Calcula una altura disponible dinámicamente usando calc (se resta un margen de 32px para evitar que se pegue al viewport)
      const maxHeight = Math.floor(viewportHeight - currentHeaderHeight - 32);
      setAvailableHeight(maxHeight);
      // Reiniciar la posición en caso de que se haya movido previamente
      setDragOffset({ x: 0, y: 0 });
    } else {
      setAnimate(false);
      const timer = setTimeout(() => setVisible(false), transitionDuration);
      return () => clearTimeout(timer);
    }
  }, [isOpen, transitionDuration]);

  // Manejo de teclado (Escape y Tab para el ciclo de foco)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (closeOnEscape && e.key === "Escape") {
        onClose();
      }
      if (e.key === "Tab" && modalRef.current) {
        const focusableElements = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
        if (!e.shiftKey && document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
        if (e.shiftKey && document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      lastActiveElement.current = document.activeElement as HTMLElement;
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, closeOnEscape, onClose]);

  // Bloquear scroll en el body mientras el modal está abierto
  useEffect(() => {
    if (preventScroll && isOpen) {
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      if (scrollBarWidth > 0) {
        document.body.style.paddingRight = `${scrollBarWidth}px`;
      }
      document.body.style.overflow = "hidden";
      document.body.style.margin = "0";
    }
    return () => {
      if (preventScroll) {
        document.body.style.overflow = "";
        document.body.style.touchAction = "";
        document.body.style.paddingRight = "";
        document.body.style.margin = "";
      }
    };
  }, [isOpen, preventScroll]);

  // Restaurar el foco al cerrar
  useEffect(() => {
    return () => {
      if (lastActiveElement.current) {
        lastActiveElement.current.focus();
      }
    };
  }, []);

  // Funciones para manejo del drag (mouse y touch)
  const handleDragStart = (e: MouseEvent | TouchEvent) => {
    if (!draggable) return;
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    dragStart.current = { x: clientX - dragOffset.x, y: clientY - dragOffset.y };
  };

  const handleDragMove = (e: MouseEvent | TouchEvent) => {
    if (!draggable || !dragStart.current) return;
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    setDragOffset({
      x: clientX - dragStart.current.x,
      y: clientY - dragStart.current.y,
    });
  };

  const handleDragEnd = () => {
    if (!draggable) return;
    dragStart.current = null;
  };

  if (!portalContainer || !visible) return null;

  return ReactDOM.createPortal(
    <div
      className={`fixed inset-0 flex ${positionClasses[position]} ${portalClassName}`}
      style={{
        zIndex: 99999,
        transition: `opacity ${transitionDuration}ms ease-in-out`,
        opacity: animate ? 1 : 0,
      }}
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        style={{
          transition: `opacity ${transitionDuration}ms ease-in-out`,
          opacity: animate ? 1 : 0,
        }}
        onClick={closeOnBackdropClick ? onClose : undefined}
      />
      
      {/* Modal Container */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={title ? "modal-title" : undefined}
        tabIndex={-1}
        className={`
          relative mx-4 my-4 flex flex-col rounded-xl bg-white shadow-xl dark:bg-gray-900
          ${size !== "full" ? sizeClasses[size] : ""}
          ${className}
        `}
        style={{
          transition: `opacity ${transitionDuration}ms ease-in-out`,
          opacity: animate ? 1 : 0,
          maxHeight: size === "full" ? "100vh" : "calc(100vh - 2rem)",
          width: size === "full" ? "100vw" : undefined,
          transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)`,
        }}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div
            ref={headerRef}
            className={`
              flex items-center justify-between border-b p-4 dark:border-gray-800
              ${draggable ? "cursor-move" : ""}
            `}
            onMouseDown={handleDragStart}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
          >
            {title && (
              <h2 id="modal-title" className="text-xl font-semibold dark:text-white">
                {title}
              </h2>
            )}
            {showCloseButton && <CloseButton onClose={onClose} size={size} />}
          </div>
        )}

        {/* Content */}
        <div
          className="flex-1 overflow-y-auto overscroll-contain p-4 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600"
          style={{
            WebkitOverflowScrolling: "touch",
          }}
        >
          {children}
        </div>
      </div>
    </div>,
    portalContainer
  );
};